import SVG from "@/app/components/pages/SVG";
import { useToast } from "@/app/hooks/toast/useToast";
import { useBreakpoint } from "@/app/hooks/useBreakpoint";
import { setFlightSearchRequestToLocalStorage } from "@/app/utils/services/service/localStorageService";
import type { FlightSearchRequest, SearchValues } from "@/types/flights";
import axios from "axios";
import clsx from "clsx";
import { size } from "lodash";
import { X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useState } from "react";
import Button from "../../Button/Button";
import DestinationField from "../DestinationField/DestinationField";
import { FlightClassWithTravellers } from "../FlightClassWithTravellers/FlightClassWithTravellers";
import CustomDatePicker from "./FlightDatePicker";

type Props = {
  values: SearchValues;
  onAddDestination: () => void;
  onRemoveDestination: (id: string) => void;
  onClearAll: () => void;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  setValues: React.Dispatch<React.SetStateAction<SearchValues>>;
  buildFlightSearchPayload: () => string;
  toLocation: string;
};

export const FlightFields = ({
  onRemoveDestination,
  onAddDestination,
  onClearAll,
  values,
  updateFlightField,
  buildFlightSearchPayload,
  setValues,
  toLocation,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const areAtLeastTwoFlights = size(values.flights) > 1;
  const isMultiCity = values.flightType === "multi_city";
  const isRoundTrip = values.flightType === "rounded";
  const breakpoint = useBreakpoint();

  const isSmallScreen = !breakpoint || breakpoint === "sm";

  const { push } = useRouter();
  const { showToast } = useToast();
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(false);

  const searchForFlights = async () => {
    try {
      setSearchLoading(true);
      const payload = buildFlightSearchPayload();
      setFlightSearchRequestToLocalStorage(JSON.stringify(values), locale);
      const { data } = await axios.post(
        `/api/flights/search?lang=${locale}`,
        payload
      );
      push(`/flights/results?searchId=${data?.searchId}`);
      setSearchLoading(false);
    } catch (err: any) {
      setSearchLoading(false);
      if (err?.response?.data?.error) {
        showToast(err.response.data.error, "error");
      } else if (err?.response?.statusText) {
        showToast(err.response.statusText, "error");
      } else {
        showToast(t("HomePageFlights.noFlightsAvailable"), "error");
      }
    }
  };

  const isFlightSearchInvalid = !values?.flights?.every(
    (flight) =>
      flight.from &&
      flight.to &&
      flight.departDate &&
      (!isRoundTrip || flight.returnDate)
  );

  return (
    <div className={clsx("flex gap-2 flex-col", !isMultiCity && "md:flex-row")}>
      {values?.flights?.map((flight, index) => (
        <div key={flight.id} className="flex-grow">
          {isMultiCity && isSmallScreen && (
            <div className="flex justify-between pt-2.5 pb-1.5">
              <h3 className="font-inter text-base text-sleap-gray-950">
                {t("HomePageFlights.tripOrder", { order: index + 1 })}
              </h3>
              {areAtLeastTwoFlights && (
                <button
                  type="button"
                  onClick={() => onRemoveDestination(flight.id)}
                  className="flex items-center text-base text-sleap-gray-700 font-inter gap-1.5 px-1.5"
                >
                  <X /> {t("Other.remove")}
                </button>
              )}
            </div>
          )}
          <div className="flex gap-2 flex-col md:flex-row">
            <div className="md:w-1/2">
              <DestinationField
                from={flight.from}
                to={flight.to}
                flightId={flight.id}
                updateFlightField={updateFlightField}
                withSwitch={!isSmallScreen && !areAtLeastTwoFlights}
                isMultiCity={isMultiCity}
                index={index}
                toLocation={toLocation}
              />
            </div>
            <div className="md:w-1/2">
              <div className="md:block sm:flex gap-x-2">
                <div className="md:w-full sm:w-2/3">
                  <CustomDatePicker
                    departOnly={values.flightType !== "rounded"}
                    departDate={flight.departDate}
                    returnDate={flight.returnDate}
                    flightId={flight.id}
                    updateFlightField={updateFlightField}
                    isFlightPage={true}
                  />
                </div>
                <div
                  className={`relative md:hidden sm:block hidden w-1/3  ${isFlightSearchInvalid ? "group" : ""}`}
                >
                  <Button
                    onClick={searchForFlights}
                    isLoading={searchLoading}
                    disabled={searchLoading || error || isFlightSearchInvalid}
                    fullWidth={true}
                  >
                    <SVG
                      alt="magnifier"
                      src="/assets/v2/icons/magnifier.svg"
                      twClasses="icon-wrapper"
                    />{" "}
                    {t("Other.search")}
                  </Button>
                  {/* Tooltip */}
                  {isFlightSearchInvalid && (
                    <div className="absolute w-44 -bottom-[52px] lg:left-1/2 sm:-right-12 right-0 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 flex flex-col items-center">
                      <div className="bg-gray-900 h-2 w-2 transform rotate-45"></div>
                      <div className="bg-gray-900 text-white py-2 px-3 text-xs rounded-md shadow-lg -mt-1">
                        {t("Other.pleaseEnterMissingInformation")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>{" "}
            <div className="sm:hidden block">
              <FlightClassWithTravellers
                values={values}
                className="w-full"
                setValues={setValues}
                isFullScreenModal={isSmallScreen}
              />
            </div>
            {!isSmallScreen && areAtLeastTwoFlights && (
              <button
                className="block bg-sleap-gray-200 hover:bg-sleap-gray-400 text-sleap-gray-950 rounded-lg p-4"
                onClick={() => onRemoveDestination(flight.id)}
              >
                <X />
              </button>
            )}
          </div>
        </div>
      ))}
      <div
        className={clsx(
          "flex flex-col gap-2 md:flex-row md:justify-between",
          isMultiCity && "pt-1"
        )}
      >
        {isMultiCity && (
          <div className="flex gap-2 w-full md:w-auto">
            {values.flights.length < 4 && (
              <Button variant="neutral" onClick={onAddDestination} fullWidth>
                {t("HomePageFlights.addDestination")}
              </Button>
            )}
            {!isSmallScreen && (
              <Button variant="white" onClick={onClearAll}>
                {t("Other.clearAll")}
              </Button>
            )}
          </div>
        )}

        <div
          className={`relative min-w-32 md:block sm:hidden ${isFlightSearchInvalid ? "group" : ""}`}
        >
          <Button
            onClick={searchForFlights}
            isLoading={searchLoading}
            disabled={searchLoading || error || isFlightSearchInvalid}
            fullWidth={true}
          >
            <SVG
              alt="magnifier"
              src="/assets/v2/icons/magnifier.svg"
              twClasses="icon-wrapper"
            />{" "}
            {t("Other.search")}
          </Button>

          {/* Tooltip */}
          {isFlightSearchInvalid && (
            <div className="absolute w-44 -bottom-[52px] lg:left-1/2 md:-right-12 right-0 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 flex flex-col items-center z-50">
              <div className="bg-gray-900 h-2 w-2 transform rotate-45"></div>
              <div className="bg-gray-900 text-white py-2 px-3 text-xs rounded-md shadow-lg -mt-1">
                {t("Other.pleaseEnterMissingInformation")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
